import {_} from 'lodash';
import {
    getHash,
    getRouteDefinition,
    getRouteDefinitions,
    getRouteLabel
} from "./constants/RouteDefinitions";
import {getRouteDefFromHash} from "./util/getRouteDefFromHash";
import {RequestRouteData} from "./req/RequestRouteData";
import {global, lm} from "./appContext";
import {IRouteDefinition, IRouteDefinitionParams, IRouteParams, TRouteIdOrDefinition} from "./index";
import {storeObservable} from "./util/sessionstore";
import {memoryStore} from "./util/memorystore";
import {getDisplayNameForRouteData, getObjectFromRouteData} from "./util/request";
import {_d} from "./util/misc";
import * as ko from "knockout"
import {crossroads} from "crossroads"
import {hasher} from "hasher"
import {KOSubscriptionTracker} from "./util/KOSubscriptionTracker";
import pubsub from "./pubsub";
import {
    buildPageShellRouteParams,
    getPageShellNavigationKey,
    getShellTabRouteDef,
    getTabHash,
    getTabIdFromParentSegment,
    isPageShellTabRoute,
    isPageShellParentRoute,
    isSoftPageShellTabNavigation,
    redirectToDefaultTab,
    resolveActiveTabId,
} from "./util/pageShellRoute";
import {expandPageShellTabRoutes} from "./util/pageShellTabRoutes";
import {applyDupOrgnrLegacyRule, routePriorityForHash} from "./util/routeOrgnrLegacy";
import {joinHashPathAndQuery, splitHashPathAndQuery} from "./util/hashQuery";
import {adjudicateEmptyRouteHash, adjudicateUnmatchedRouteHash, isEmptyRoutePath, navigateToAppHome, stripRouteHash} from "./auth/navigateAfterLogin";

/** Crossroads priority for expanded PageShell tab hashes (must beat parent /:options:/ patterns). */
const PAGE_SHELL_TAB_ROUTE_PRIORITY = 0;

export default class Router {

    public currentRoute: KnockoutObservable<IRouteDefinitionParams | null> = ko.observable(null);

    // FIXME - make this private
    public requestRouteData: RequestRouteData | null = null;
    private pageTitle: KnockoutObservable<string> = ko.observable("eAdm");
    private routeData: any;
    public onHashChange?: (newHash: string, oldHash: string) => Promise<string>
    public oldHash: string | null = null

    private readonly kosub = new KOSubscriptionTracker()

    init() {

        const addRoute = (hash: string | RegExp, routeDef: IRouteDefinition, priority = -1) => {
            const route = crossroads.addRoute(hash, (routeParams: IRouteParams) => {
                let matchedDef = routeDef;
                memoryStore.get('show.app.interact')(matchedDef.showAppInteract ?? true)

                if (isPageShellParentRoute(matchedDef)) {
                    const tabId = getTabIdFromParentSegment(matchedDef, routeParams);
                    if (tabId) {
                        matchedDef = getShellTabRouteDef(matchedDef, tabId);
                    } else if (redirectToDefaultTab(matchedDef.id, routeParams)) {
                        // URL was canonicalized to a tab hash; the re-parse will mount it.
                        return;
                    } else {
                        // No org yet to build the canonical hash: keep the bare deep link but still
                        // activate the default tab in memory so the route (and any not-logged-in
                        // overlay) mounts. Org-prefixing happens after login re-asserts the hash.
                        const defaultTabId = resolveActiveTabId(matchedDef, routeParams);
                        if (defaultTabId) {
                            matchedDef = getShellTabRouteDef(matchedDef, defaultTabId);
                        }
                    }
                }

                const isShellTab = isPageShellTabRoute(matchedDef);
                const shellParams = isShellTab
                    ? buildPageShellRouteParams(matchedDef, routeParams)
                    : null;
                const effectiveDef = shellParams?.__shellRouteDef ?? matchedDef;

                const __routeParams: IRouteDefinitionParams = shellParams ?? {
                    ...(routeParams['id'] ? {__id: Number(routeParams['id'])} : {}),
                    __routeDef: matchedDef,
                    __routeId: matchedDef.id,
                    __routeParams: routeParams,
                };

                const prevRoute = this.currentRoute();
                // Same shell+entity: never hard-set currentRoute — `_component` remounts on notify and
                // RouteValidator's cache still has the previous tab's params (URL moves, UI stays).
                if (prevRoute && isSoftPageShellTabNavigation(prevRoute, __routeParams)) {
                    this.applySoftPageShellTabNavigation(prevRoute, __routeParams);
                    return;
                }

                // Same shell+entity without soft-nav (e.g. overview remount): keep in-flight getobject.
                const sameShellEntity = !!getPageShellNavigationKey(prevRoute)
                    && getPageShellNavigationKey(prevRoute) === getPageShellNavigationKey(__routeParams);

                if (!sameShellEntity) {
                    this.requestRouteData?.abort?.();
                    this.requestRouteData = null;
                    this.routeData = null;
                }
                if (effectiveDef.fetchRouteDef && global.login.isLoggedIn()) {
                    if (!sameShellEntity || !this.requestRouteData) {
                        this.requestRouteData = new RequestRouteData(effectiveDef, routeParams);
                    }
                    __routeParams.__requestRouteData = this.requestRouteData;
                    if (effectiveDef.isSubitem && storeObservable('latest.objects.automatic')()) {
                        this.requestRouteData
                            .request()
                            .then((data: any[]) => {
                                global.latestObjectsManager.add(data, effectiveDef, routeParams);
                                this.routeData = getObjectFromRouteData(data);
                                this.updatePageTitle();
                                hasher.replaceHash(hasher.getHash())
                            })
                            .catch((reason: string) => _d.error(reason))
                    }
                }
                this.currentRoute(__routeParams);

                // Defer title: hasher fires `changed` before the new history entry exists, so a
                // sync document.title write would stamp the new title onto the previous entry.
                this.schedulePageTitleUpdate();
                hasher.replaceHash(hasher.getHash())

                // flush any route data if it's the same as last time we changed route
                const _old = memoryStore.get('oldRouteData');
                const _new = memoryStore.get('routeData');
                const _oldV = _old();
                const _newV = _new();
                if (_newV == _oldV) {
                    _old(_newV);
                    _new(null);
                }
                _old(_newV);

                document.documentElement.scrollTo({top: 0});
            }, priority);
            applyDupOrgnrLegacyRule(route, hash);
        }

        // Register routes with crossroads.js
        getRouteDefinitions().forEach((routeDef: IRouteDefinition) => {
            if (!('hash' in routeDef) && !('linkRegexp' in routeDef)) {
                return;
            }

            const arr: (string | RegExp)[] = []
            if (routeDef.hash) {
                if (typeof routeDef.hash == 'string') {
                    arr.push(routeDef.hash)
                } else {
                    arr.push(...routeDef.hash)
                }
            }
            if (routeDef.linkRegexp) {
                if (Array.isArray(routeDef.linkRegexp)) {
                    arr.push(...routeDef.linkRegexp)
                } else {
                    arr.push(routeDef.linkRegexp)
                }
            }
            arr.forEach(hash => addRoute(hash, routeDef, routePriorityForHash(hash, -1)))
        });

        expandPageShellTabRoutes().forEach(({hash, routeDef}) =>
            addRoute(hash, routeDef, routePriorityForHash(hash, PAGE_SHELL_TAB_ROUTE_PRIORITY)),
        );

        // @ts-ignore
        crossroads.normalizeFn = crossroads.NORM_AS_OBJECT;
        hasher.initialized.add(this.parseHash.bind(this));
        hasher.changed.add(this.parseHash.bind(this));
        hasher.init();

        pubsub.on(this, 'login.success.final', () => this.recordLatestObjectAfterLogin());

        this.updatePageTitle()
        this.kosub.subscribe(lm.selectedLanguage, () => {
            this.updatePageTitle()
            requestAnimationFrame(() => this.updatePageTitle())
        })
        this.kosub.subscribe(lm.isReady, () => {
            this.updatePageTitle()
            requestAnimationFrame(() => this.updatePageTitle())
        })
    }

    /**
     * Tab hash change under the same PageShell + entity: keep shell/heading alive.
     * Updates route metadata + active tab only (no abort/refetch/remount/scroll).
     * Does not notify currentRoute subscribers — `_component` remounts on any update.
     */
    private applySoftPageShellTabNavigation(
        prevRoute: IRouteDefinitionParams,
        nextRoute: IRouteDefinitionParams,
    ): void {
        const tabId = nextRoute.__tabRouteDef?.id ?? nextRoute.__routeId;
        if (nextRoute.__routeId != null) {
            prevRoute.__routeId = nextRoute.__routeId;
        }
        if (nextRoute.__tabRouteDef != null) {
            prevRoute.__tabRouteDef = nextRoute.__tabRouteDef;
        }
        if (nextRoute.__routeParams != null) {
            prevRoute.__routeParams = nextRoute.__routeParams;
        }
        if (nextRoute.__id != null) {
            prevRoute.__id = nextRoute.__id;
        }
        // Prefer live route tabManager; fall back to RouteValidator cache (create flows can lose the
        // attach race when the first paint used cloned component params).
        const tabManager = prevRoute.__tabManager
            ?? global.routeValidator.getCachedPageShellTabManager()
            ?? null;
        if (tabManager && !prevRoute.__tabManager) {
            prevRoute.__tabManager = tabManager;
        }
        if (tabId && tabManager) {
            tabManager.setTabId(tabId);
        }
        this.schedulePageTitleUpdate();
    }

    /** Run after hasher finishes writing history so title binds to the current entry only. */
    private schedulePageTitleUpdate() {
        queueMicrotask(() => this.updatePageTitle());
    }

    public parseHash(newHash: string) {
        const isHashChanged = newHash != (this.oldHash ?? '')
        if (this.onHashChange && isHashChanged) {
            this.onHashChange(newHash, this.oldHash ?? '')
                .then((hash: string) => {
                    const doParseNewHash = hash == newHash;
                    if (doParseNewHash) {
                        this._parseHash(hash);
                    } else {
                        hasher.setHash(hash)
                    }
                })
        } else {
            this._parseHash(newHash)
        }
    }

    private _parseHash(newHash: string) {
        // Hash queries (?key=val) are app state, not Crossroads segments — strip before match.
        const {path: pathHash, search: hashSearch} = splitHashPathAndQuery(newHash);
        const withSearch = (path: string) => joinHashPathAndQuery(path, hashSearch);
        const currentOrgId = global.login.selectedOrganizationId()

        // Empty / nowhere → session home or formlogin (replaceHash inside helper).
        if (isEmptyRoutePath(pathHash)) {
            adjudicateEmptyRouteHash(hashSearch);
            return;
        }

        // Deep-link logout command (boot and mid-session); GUI hide is via isLoggingOut / chromeReady.
        if (/^logout\/?$/i.test(pathHash)) {
            this.oldHash = newHash;
            void global.login.doLogout();
            return;
        }

        // Route prefix owns working org (path validity does not matter). Skip URL rewrite — hash already has org.
        const regExp_org = /^o\/(.*?)\//i
        const regExpExecArray_org = regExp_org.exec(pathHash);
        if (regExpExecArray_org?.length) {
            const orgId = regExpExecArray_org[1];
            if (orgId) {
                global.login.syncSelectedOrganizationFromRoute(orgId);
            }
        }

        const regExp_noOrg = /^(?!o\/)(.*)/i
        const regExpExecArray_noOrg = regExp_noOrg.exec(pathHash);
        const routeDef = getRouteDefFromHash(pathHash)
        // Only rewrite known routes that want an org prefix — not unmatched junk.
        if (regExpExecArray_noOrg?.length && !!currentOrgId && routeDef && routeDef.hashPrefixOrgId !== false) {
            const replaceHash = `o/${currentOrgId}/${regExpExecArray_noOrg[1]}`
            hasher.replaceHash(withSearch(replaceHash))
            return
        }

        const user = global?.login?.user?.();
        const homeOrg = user ? global.login.getHomeOrg() : undefined;
        if (user && homeOrg) {
            if (/^resetownpassword\/?$/.test(pathHash)) {
                const replaceHash = getHash('resetownpassword', {
                    id: user.id,
                    orgnr: homeOrg.organizationid
                }, false);
                if (replaceHash) {
                    hasher.replaceHash(withSearch(replaceHash))
                }
                return
            }

            if (/^myprofile\/?$/.test(pathHash)) {
                const replaceHash = getHash('userdetails', {
                    id: user.id,
                    orgnr: homeOrg.organizationid
                }, false);
                if (replaceHash) {
                    hasher.replaceHash(withSearch(replaceHash))
                }
                return
            }

            if (/^mymessages\/?$/.test(pathHash)) {
                const replaceHash = getTabHash('tasksandmessages', {
                    id: user.id,
                    orgnr: homeOrg.organizationid,
                });
                if (replaceHash) {
                    hasher.replaceHash(withSearch(stripRouteHash(replaceHash)));
                }
                return;
            }
        }

        // Logged-in visit to formlogin → same cascade as post-login home.
        if (routeDef?.id === 'formlogin' && global.login.isLoggedIn()) {
            navigateToAppHome();
            return;
        }

        // Unknown path: anonymous → formlogin; logged in → crossroads http400 catch-all.
        if (adjudicateUnmatchedRouteHash(pathHash, hashSearch)) {
            return;
        }

        this.oldHash = newHash
        crossroads.parse(pathHash);
    }

    public updatePageTitle() {
        if (!lm.isReady()) {
            this.pageTitle('eAdm')
            return
        }
        const cr = this.currentRoute();
        if (!cr?.__routeDef) {
            return;
        }
        // Shell owns entity identity; tab (when present) is a separate segment, not the display-name source.
        const parts: string[] = [];
        let shellLabel = lm.t(getRouteLabel(cr.__routeDef)) ?? '';
        if (this.routeData) {
            const displayNameForRouteData = getDisplayNameForRouteData(this.routeData, cr.__routeDef, cr.__routeParams ?? {});
            shellLabel += ` [${displayNameForRouteData}]`;
        }
        if (shellLabel) {
            parts.push(shellLabel);
        }
        const tabDef = cr.__tabRouteDef;
        if (tabDef && tabDef.id !== cr.__routeDef.id) {
            const tabLabel = lm.t(getRouteLabel(tabDef));
            if (tabLabel) {
                parts.push(tabLabel);
            }
        }
        if (!parts.some(part => part.includes('eAdm'))) {
            parts.push('eAdm');
        }
        // Drop consecutive duplicate segments (Users - Users - eAdm → Users - eAdm).
        const deduped: string[] = [];
        for (const part of parts) {
            if (deduped[deduped.length - 1] === part) {
                continue;
            }
            deduped.push(part);
        }
        this.pageTitle(deduped.join(' - '));
    }

    isCurrentRoute(idOrDef: TRouteIdOrDefinition) {
        const routeDef = getRouteDefinition(idOrDef);
        const currentDef = this.currentRoute()?.__routeDef;
        return !!routeDef && !!currentDef && routeDef.id == currentDef.id;
    }

    /**
     * On cold load the hash is parsed before login; route data may load only on the detail component.
     * Re-run router fetch after login so page title and latest-objects stay in sync with direct URLs.
     */
    private recordLatestObjectAfterLogin() {
        const cr = this.currentRoute();
        const routeDef = cr?.__routeDef;
        const routeParams = cr?.__routeParams;
        if (!global.login.isLoggedIn()
            || !routeDef?.fetchRouteDef
            || !routeDef.isSubitem
            || !storeObservable('latest.objects.automatic')()) {
            return;
        }
        if (!cr || this.routeData || this.requestRouteData) {
            return;
        }
        this.requestRouteData = new RequestRouteData(routeDef, routeParams ?? {});
        cr.__requestRouteData = this.requestRouteData;
        this.requestRouteData
            .request()
            .then((data: any[]) => {
                global.latestObjectsManager.add(data, routeDef, routeParams ?? {});
                this.routeData = getObjectFromRouteData(data);
                this.updatePageTitle();
            })
            .catch((reason: string) => _d.error(reason));
    }

}
